import { Stack } from '@mui/material';
import { ClockCounterClockwiseIcon, PlusIcon, XIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Text, api, getQueryProps, isDefined, isTruthy } from 'common';

import { AgenticChatToolbarTitle } from './ToolbarTitle';
import { AgenticChatLogo } from '../../components/AgenticChat/Logo';
import { AgenticChatRecentChatsMenu } from '../../components/AgenticChat/RecentChatsMenu';
import { AgenticChatToolbarButton } from '../../components/AgenticChat/ToolbarButton';
import { AgenticChatViewModeButton } from '../../components/AgenticChat/ViewModeButton';
import { AGENTIC_CHAT_PRODUCT_NAME } from '../../components/AgenticChat/constants';
import { useAgenticChatContext } from '../../components/AgenticChat/context';

export function AgenticChatToolbar() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const {
    sessionId,
    createSessionMutation,
    startNewChat,
    closeAgenticChat,
    viewMode,
    setViewMode,
  } = useAgenticChatContext();
  // Cache-only reads: Content loads the history and refetches this list after each run.
  const { data: messages } = useQuery({
    ...getQueryProps(api.client.AgenticPlatformSession, 'findAllMessages', sessionId ?? ''),
    enabled: false,
  });
  const { data: sessionsWithMessages } = useQuery({
    ...getQueryProps(api.client.AgenticPlatformSession, 'findAll', {
      filterMode: 'OnlySessionsWithMessages',
    }),
    enabled: false,
  });
  const hasMessages =
    isDefined(messages) &&
    (isTruthy(messages.length) ||
      isDefined(sessionsWithMessages?.find((session) => session.id === sessionId)));

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      aria-label={AGENTIC_CHAT_PRODUCT_NAME}
      sx={{
        flexShrink: 0,
        padding: 1.5,
        flexWrap: 'wrap',
        backgroundColor: 'agenticChat.main',
        color: 'agenticChat.contrastText',
      }}
    >
      <Stack direction="row" alignItems="center" gap={0.75} sx={{ minWidth: 0, flex: 1 }}>
        <AgenticChatLogo sx={{ fontSize: 16, flexShrink: 0 }} />
        {isDefined(sessionId) && hasMessages ? (
          <AgenticChatToolbarTitle key={sessionId} sessionId={sessionId} />
        ) : (
          <Text variant="body2" bold color="inherit" noWrap>
            {AGENTIC_CHAT_PRODUCT_NAME}
          </Text>
        )}
      </Stack>
      <Stack direction="row" gap={1} sx={{ flexShrink: 0, marginLeft: 'auto' }}>
        {isDefined(sessionId) && hasMessages && (
          <AgenticChatToolbarButton
            label="New chat"
            icon={<PlusIcon />}
            loading={createSessionMutation.isPending}
            onClick={() => startNewChat()}
          />
        )}
        <AgenticChatToolbarButton
          label="Recent chats"
          icon={<ClockCounterClockwiseIcon />}
          aria-haspopup="menu"
          aria-expanded={isDefined(anchor)}
          aria-controls={isDefined(anchor) ? 'toolbar-recent-chats' : undefined}
          onClick={(event) => setAnchor(event.currentTarget)}
        />
        <AgenticChatViewModeButton
          viewMode={viewMode}
          onToggle={() => setViewMode(viewMode === 'Sidebar' ? 'Fullscreen' : 'Sidebar')}
        />
        <AgenticChatToolbarButton
          label={`Close ${AGENTIC_CHAT_PRODUCT_NAME}`}
          icon={<XIcon />}
          onClick={() => closeAgenticChat()}
        />
      </Stack>
      <AgenticChatRecentChatsMenu
        id="toolbar-recent-chats"
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      />
    </Stack>
  );
}
